import React from "react";
import { Box, Button, Dialog as MuiDialog, IconButton, Typography } from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import Dialog from "../Dialog/Dialog";
import ImageCrop from "../ImageCrop/ImageCrop";

interface Props {
  onSelect: (image: Blob, index?: number) => void;
  initialImages?: string[];
  isUploading?: boolean;
  onRemove?: (index?: number) => void;
  maxAttachments?: number;
}

interface AttachmentItem {
  id: string;
  url: string;
}

const AttachmentUploadInput = ({
  onSelect,
  initialImages,
  isUploading = false,
  onRemove,
  maxAttachments = 3
}: Props): React.JSX.Element => {
  const imageRef = React.useRef<HTMLInputElement>(null);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [cropperOpen, setCropperOpen] = React.useState(false);
  const [cropperImage, setCropperImage] = React.useState<string | null>(null);
  const [deleteIndex, setDeleteIndex] = React.useState<number | null>(null);
  const [previewImage, setPreviewImage] = React.useState<string | null>(null);

  const createInitialItems = (): AttachmentItem[] => {
    if (initialImages && initialImages.length > 0) {
      return initialImages.filter(Boolean).map((url, i) => ({ id: `init-${i}-${url}`, url }));
    }
    return [];
  };

  const [attachments, setAttachments] = React.useState<AttachmentItem[]>(createInitialItems);

  const prevInitialImagesRef = React.useRef(initialImages);

  React.useEffect(() => {
    if (initialImages !== undefined && initialImages !== prevInitialImagesRef.current) {
      setAttachments(
        initialImages.filter(Boolean).map((url, i) => ({ id: `init-${i}-${url}`, url }))
      );
      prevInitialImagesRef.current = initialImages;
    }
  }, [initialImages]);

  function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (attachments.length >= maxAttachments) {
      const { target } = e;
      if (target) {
        target.value = "";
      }
      return;
    }

    if (file.size >= 2097152) {
      setDialogOpen(true);
      const { target } = e;
      if (target) {
        target.value = "";
      }
      return;
    }

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif"].includes(ext || "")) {
      setCropperImage(URL.createObjectURL(file));
      setCropperOpen(true);
    } else {
      setDialogOpen(true);
    }

    const { target } = e;
    if (target) {
      target.value = "";
    }
  }

  const handleDelete = () => {
    if (deleteIndex !== null) {
      const targetItem = attachments[deleteIndex];
      if (targetItem?.url && targetItem.url.startsWith("blob:")) {
        URL.revokeObjectURL(targetItem.url);
      }
      const updated = attachments.filter((_, i) => i !== deleteIndex);
      setAttachments(updated);
      onRemove?.(deleteIndex);
      setDeleteIndex(null);
    }
  };

  return (
    <Box display="flex" width="100%" flexDirection="column">
      {deleteIndex !== null && (
        <Dialog
          title="Delete Attachment"
          description={`Are you sure you want to delete attachment ${deleteIndex + 1}?`}
          next={handleDelete}
          cancel={() => setDeleteIndex(null)}
          readMode={false}
        />
      )}

      {previewImage && (
        <MuiDialog
          open={Boolean(previewImage)}
          onClose={() => setPreviewImage(null)}
          maxWidth="md"
          fullWidth
        >
          <Box sx={{ position: "relative", p: 2, textAlign: "center", backgroundColor: "#fff" }}>
            <img
              src={previewImage}
              alt="Attachment preview"
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: "4px"
              }}
            />
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => setPreviewImage(null)} variant="outlined" size="small">
                Close
              </Button>
            </Box>
          </Box>
        </MuiDialog>
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          alignItems: "center"
        }}
      >
        {attachments.length > 0 && (
          <Box width="100%" display="flex" flexDirection="column" gap="10px">
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#555" }}>
                Attachments ({attachments.length}/{maxAttachments})
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  attachments.length === 1 ? "1fr" : "repeat(auto-fit, minmax(85px, 1fr))",
                gap: "10px",
                width: "100%"
              }}
            >
              {attachments.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px",
                    width: "100%"
                  }}
                >
                  <Box
                    sx={{
                      height: attachments.length === 1 ? "110px" : "85px",
                      width: "100%",
                      borderRadius: "6px",
                      border: "1px solid #d0d7de",
                      backgroundColor: "#f6f8fa",
                      textAlign: "center",
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: "#1976d2",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.08)"
                      }
                    }}
                    onClick={() => setPreviewImage(item.url)}
                    title="Click to preview"
                  >
                    <img
                      src={item.url}
                      alt={`Attachment ${index + 1}`}
                      style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                    />
                    <IconButton
                      size="small"
                      disabled={isUploading}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteIndex(index);
                      }}
                      aria-label={`Remove attachment ${index + 1}`}
                      title="Remove attachment"
                      sx={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        color: "#d32f2f",
                        padding: "3px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        "&:hover": {
                          backgroundColor: "#ffebee",
                          color: "#b71c1c"
                        }
                      }}
                    >
                      <DeleteOutline sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </Box>
                  <Button
                    type="button"
                    size="small"
                    disabled={isUploading}
                    onClick={() => setDeleteIndex(index)}
                    startIcon={<DeleteOutline sx={{ fontSize: "14px !important" }} />}
                    sx={{
                      fontSize: "11px",
                      padding: "2px 6px",
                      color: "#d32f2f",
                      textTransform: "none",
                      minWidth: 0,
                      lineHeight: 1.2,
                      "&:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.08)"
                      }
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: "10px" }}>
          <Button
            disabled={isUploading || attachments.length >= maxAttachments}
            size="small"
            variant="contained"
            sx={{ width: "100%" }}
            onClick={() => imageRef?.current?.click()}
          >
            {isUploading ? "Uploading..." : "Upload Attachment"}
          </Button>
        </Box>
      </Box>

      <ImageCrop
        isSaving={isUploading}
        image={cropperImage}
        isOpen={cropperOpen}
        dynamicRatio={false}
        onCancel={() => {
          setCropperOpen(false);
          if (cropperImage && cropperImage.startsWith("blob:")) {
            URL.revokeObjectURL(cropperImage);
          }
          setCropperImage(null);
        }}
        onSave={(img) => {
          if (cropperImage && cropperImage.startsWith("blob:")) {
            URL.revokeObjectURL(cropperImage);
          }
          const newUrl = URL.createObjectURL(img);
          const newItem: AttachmentItem = {
            id: `crop-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            url: newUrl
          };
          setAttachments((prev) => {
            const next = [...prev, newItem];
            return next;
          });
          onSelect(img, attachments.length);
          setCropperOpen(false);
          setCropperImage(null);
        }}
      />

      <input
        type="file"
        accept="image/jpg,image/jpeg,image/png,image/gif"
        ref={imageRef}
        style={{ display: "none" }}
        onChange={handleImageChange}
        disabled={isUploading || attachments.length >= maxAttachments}
      />

      {dialogOpen && (
        <Dialog
          title="Cannot upload image"
          description="File size limit is 2MB. Accepted formats: JPG, JPEG, PNG, GIF."
          next={() => setDialogOpen(false)}
          readMode
        />
      )}
    </Box>
  );
};

export default AttachmentUploadInput;
